import { type FC, useEffect, useState } from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { type ConfigOverrideDialogOption } from './ConfigSection';

const CompactRadio = styled(Radio)({
    paddingTop: 4,
    paddingBottom: 4
});

type Props = {
    open: boolean;
    selected: string;
    onClose: () => void;
    onSelect: (key: string) => void;
    configOverrideOption: ConfigOverrideDialogOption;
};

const ConfigOverrideDialog: FC<Props> = ({ open, selected, onClose, onSelect, configOverrideOption }) => {
    const { t } = useTranslation('admin_settings', { useSuspense: false });
    const [localSelected, setLocalSelected] = useState(selected);

    useEffect(() => {
        if (open) {
            setLocalSelected(selected);
        }
    }, [open, selected]);

    const handleSave = () => {
        onSelect(localSelected);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{configOverrideOption.title}</DialogTitle>

            <DialogContent>
                {configOverrideOption.description && (
                    <Typography variant="h6">{configOverrideOption.description}</Typography>
                )}
                <RadioGroup value={localSelected} onChange={(e) => setLocalSelected(e.target.value)}>
                    {configOverrideOption.options.map((option) => (
                        <FormControlLabel
                            key={option.key}
                            value={option.key}
                            control={<CompactRadio />}
                            label={option.label}
                        />
                    ))}
                </RadioGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfigOverrideDialog;
